import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, Gamepad2, Star, User, Volume2, VolumeX, ArrowLeft, Tag, LifeBuoy } from "lucide-react";
import { isMuted, setMuted } from "@/lib/speech";

const links = [
  { to: "/", label: "Home", icon: Home, id: "home" },
  { to: "/learn", label: "Learn", icon: BookOpen, id: "learn" },
  { to: "/play", label: "Play", icon: Gamepad2, id: "play" },
  { to: "/progress", label: "Progress", icon: Star, id: "progress" },
  { to: "/parent", label: "Parent", icon: User, id: "parent" },
  { to: "/pricing", label: "Pricing", icon: Tag, id: "pricing" },
  { to: "/help", label: "Help / Contact Us", short: "Help", icon: LifeBuoy, id: "help" },
];

export function TopBar({ title, backTo }) {
  const nav = useNavigate();
  const [muted, setM] = React.useState(isMuted());
  const toggle = () => { const v = !muted; setMuted(v); setM(v); };
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-white/70 border-b border-purple-100">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {backTo ? (
            <button
              data-testid="back-button"
              onClick={() => nav(backTo)}
              className="ll-speaker"
              aria-label="Back"
            >
              <ArrowLeft size={20} />
            </button>
          ) : (
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center shadow-lg text-white font-bold text-xl">L</div>
          )}
          <div>
            <div className="font-display font-bold text-lg text-purple-900 leading-none">{title || "LittleLearner"}</div>
            <div className="text-[11px] text-purple-500 font-semibold tracking-widest uppercase">Age 2–5</div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-1 bg-purple-50 rounded-full p-1">
          {links.map(l => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) => `px-4 py-2 rounded-full font-semibold text-sm transition ${isActive ? "bg-white text-purple-700 shadow" : "text-purple-500 hover:text-purple-700"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <button
          data-testid="audio-toggle-button"
          onClick={toggle}
          className="ll-speaker"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>
    </header>
  );
}

export function BottomBar() {
  const loc = useLocation();
  return (
    <nav className="md:hidden fixed bottom-3 left-3 right-3 z-40">
      <div className="ll-card flex items-center justify-between px-3 py-2 rounded-[28px]">
        {links.map(l => {
          const Icon = l.icon;
          const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
          return (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`bottom-nav-${l.id}`}
              className={`flex flex-col items-center gap-1 px-2 sm:px-3 py-1.5 rounded-2xl transition ${active ? "bg-purple-100 text-purple-700" : "text-purple-400"}`}
            >
              <Icon size={22} strokeWidth={2.4} />
              <span className="text-[10px] font-bold">{l.short || l.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
